import React from "react";
import { Link } from "react-router-dom";
import { Zap, Bell, MapPin, Layers, Download, Sparkles, ShieldCheck, TrendingUp, Clock, ArrowRight, Hexagon } from "lucide-react";
import { MarketingShell } from "@/components/marketing/MarketingShell";
import { BentoCard, BrutalButton } from "@/components/dashboard/BentoCard";

const FEATURES = [
  {
    icon: Sparkles,
    title: "AI Deal Score",
    body: "Every listing gets a 0–100 score based on equity, condition, urgency, and sale probability. Sort the whole nation by 'hottest first' in one click.",
    tone: "red",
  },
  {
    icon: Bell,
    title: "Real-time + daily alerts",
    body: "Save any filter combo. Get emailed the moment a new listing matches — or a daily digest, or both. You choose per search.",
    tone: "yellow",
  },
  {
    icon: MapPin,
    title: "Nationwide coverage",
    body: "Every US state · top 5 cities per state · live adapters for LA, NYC, Chicago, Nashville, Chattanooga, Houston, and Phoenix. More every month.",
    tone: "yellow",
  },
  {
    icon: Layers,
    title: "Real public-record data",
    body: "We fetch straight from county PDFs, Socrata APIs, and treasurer download files. No scraped listings sites — the primary sources.",
    tone: "red",
  },
  {
    icon: Download,
    title: "CSV + JSON export",
    body: "Subscriber-only. Filter down to your buy box, hit export, take the shortlist straight into your CRM or Excel.",
    tone: "yellow",
  },
  {
    icon: Clock,
    title: "Auto-refresh every 30 min",
    body: "Background scheduler re-hits every enabled source so you're never seeing yesterday's data on auction morning.",
    tone: "red",
  },
];

export default function FeaturesPage() {
  return (
    <MarketingShell>
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 border-2 border-black bg-yellow-300 font-mono-data text-[10px] uppercase tracking-widest">
            <Zap size={12} /> What you get
          </div>
          <h1 className="mt-6 font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
            Everything you need to <span className="text-red-600">win the auction</span> —
            before it starts.
          </h1>
          <p className="mt-5 text-lg text-zinc-700 max-w-xl">
            Six weapons in your kit. All included on every plan. No feature gates,
            no upsells — the paywall is just fresh data + export.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-2 gap-5">
        {FEATURES.map((f) => (
          <BentoCard key={f.title} className="!p-6" data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 border-2 border-black flex items-center justify-center shrink-0 ${f.tone === "red" ? "bg-red-600 text-white" : "bg-yellow-400 text-black"}`}>
                <f.icon size={22} strokeWidth={2.25} />
              </div>
              <div className="min-w-0">
                <div className="font-display text-xl font-bold tracking-tight">{f.title}</div>
                <p className="mt-2 text-sm text-zinc-700 leading-relaxed">{f.body}</p>
              </div>
            </div>
          </BentoCard>
        ))}
      </section>

      <section className="bg-brand-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 border-2 border-yellow-400 text-yellow-400 font-mono-data text-[10px] uppercase tracking-widest">
            <TrendingUp size={12} /> Try it free
          </div>
          <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight">
            The shortlist is <span className="text-yellow-400">3 days away.</span>
          </h2>
          <p className="mt-4 text-white/80 max-w-lg mx-auto">
            Free 3-day trial. Full access. Cancel any time — even mid-trial and you
            keep everything until the trial ends.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <BrutalButton as={Link} to="/login" data-testid="features-cta-primary">
              Start free trial <ArrowRight size={14} />
            </BrutalButton>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-yellow-400 text-yellow-400 font-display font-semibold text-sm hover:bg-yellow-400 hover:text-black"
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
